import CertificationRequestQ from '@/queries/CertificationRequestQ'
import CourseQ from '@/queries/CourseQ'
import {
  CertificationRequestType,
  MATH_DOMAIN,
  SCIENCE_DOMAIN,
  TopicType,
} from '@/queries/constants'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { type Query, SQL } from '@/client/query'

import type { CourseCertificationItem } from './types'

const FULLY_MASTERED = '100% Mastery'

export function courseCertificationItemsFromQuery({
  user,
}: {
  user: Query
}): CourseCertificationItem[] {
  const certificationRequests = user
    .many(
      CertificationRequestQ.where(
        {
          userId: UserQ.id,
          include: [
            {
              association: 'Topic',
              required: true,
              where: TopicQ.isActive(),
              include: [
                {
                  association: 'Course',
                  required: true,
                },
              ],
            },
          ],
          reviewed: 'approved',
        },
        SQL`(NOT ${CourseQ.isSATBootcampCourse()})`
      )
    )
    .map(certificationRequest => ({
      courseName: certificationRequest(CourseQ.name) ?? '',
      courseId: certificationRequest(CourseQ.id) ?? 0,
      courseWeight: certificationRequest(CourseQ.weight) ?? 0,
      courseIcon: certificationRequest(CourseQ.icon) ?? '',
      courseDomain: certificationRequest(CourseQ.domain) ?? '',
      topicName: certificationRequest(TopicQ.name) ?? '',
      topicIcon: certificationRequest(TopicQ.icon) ?? '',
      type: certificationRequest(CertificationRequestQ.type) ?? CertificationRequestType.Video,
      topicType: certificationRequest(TopicQ.type) ?? TopicType.unit,
    }))

  // Always aggregate math and science (hidden) courses by course
  const aggregateCourseIdToCountTopics: Record<number, number> = {}
  user
    .many(
      CourseQ.where(
        {},
        SQL`(${CourseQ.domain} = ${MATH_DOMAIN} OR ${CourseQ.domain} = ${SCIENCE_DOMAIN})`
      )
    )
    .forEach(course => {
      const courseId = course(CourseQ.id) ?? 0
      const countTopicsInCourse = course`COUNT(*) ${TopicQ.where({
        courseId: CourseQ.id,
        type: TopicType.unit,
      })}`
      aggregateCourseIdToCountTopics[courseId] = countTopicsInCourse
    })

  const aggregateCourseIds = Object.keys(aggregateCourseIdToCountTopics).map(id =>
    Number.parseInt(id, 10)
  )

  const aggregateCertificationItems: CourseCertificationItem[] = aggregateCourseIds
    .filter(courseId =>
      certificationRequests.some(
        r => r.courseId === courseId && [TopicType.challenge, TopicType.unit].includes(r.topicType)
      )
    )
    .map(courseId => {
      const certificationsInCourse = certificationRequests.filter(r => r.courseId === courseId)
      const firstCertification = certificationsInCourse[0]
      const hasCourseChallenge = certificationsInCourse.some(
        r => r.topicType === TopicType.challenge
      )
      const countUnitCertifications = certificationsInCourse.filter(
        r => r.topicType === TopicType.unit
      ).length
      const totalTopicsInCourse = aggregateCourseIdToCountTopics[courseId]
      if (countUnitCertifications === totalTopicsInCourse || hasCourseChallenge) {
        return {
          name: firstCertification.courseName,
          icon: firstCertification.courseIcon,
          label: FULLY_MASTERED,
        }
      }
      return {
        name: firstCertification.courseName,
        icon: firstCertification.courseIcon,
        label: `${countUnitCertifications} / ${totalTopicsInCourse} Topics`,
      }
    })

  const sortedAggregateCertificationItems = aggregateCertificationItems
    .filter(req => req.label === FULLY_MASTERED)
    .concat(aggregateCertificationItems.filter(req => req.label !== FULLY_MASTERED))

  // For other courses, aggregate by course only if the user has a course challenge certification
  // Otherwise, aggregate by topic
  const nonAggregateCourseCertificationItems: CourseCertificationItem[] = certificationRequests
    .filter(
      req => !aggregateCourseIds.includes(req.courseId) && req.topicType === TopicType.challenge
    )
    .map(req => ({
      name: req.courseName,
      icon: req.courseIcon,
      label: req.type === CertificationRequestType.Honorary ? 'Honorary' : '',
    }))

  const nonAggregateCertificationItems: CourseCertificationItem[] = certificationRequests
    .filter(
      req =>
        req.topicType === TopicType.ap ||
        (!aggregateCourseIds.includes(req.courseId) && req.topicType !== TopicType.challenge)
    )
    .map(req => ({
      name: req.topicName,
      icon: req.courseIcon,
      label: req.type === CertificationRequestType.Honorary ? 'Honorary' : '',
    }))

  return [
    ...sortedAggregateCertificationItems,
    ...nonAggregateCourseCertificationItems,
    ...nonAggregateCertificationItems,
  ]
}
